import './VDRAccess.scss';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import ReactSwitch from 'react-switch';
import { EngagementBuyer } from '../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { Timeline } from '../../../slices/marketplace/timeline/models/timeline.model';
import { UpdateBuyerIsVdrAvailable } from '../../../slices/marketplace/timeline/thunks/update-buyer-is-vdr-available.thunk';

interface VDRAccessProps {
    currentEngagement: Timeline;
    availableBuyers?: EngagementBuyer[];
}

const VDRAccess = (props: VDRAccessProps) => {
    const dispatch = useDispatch() as any;
    const [featureStates, setFeatureStates] = useState<Record<number, boolean>>(
        {}
    );
    
    const getBuyersWithDataNdaSigned = (): EngagementBuyer[] => {
        const buyers = props.currentEngagement?.engagementProcess?.buyers || [];
        const availableBuyers = props.availableBuyers || [];
        const allBuyers = [...buyers, ...availableBuyers];
        const uniqueBuyers = Array.from(
            new Map(allBuyers.map((b) => [b.buyerId, b])).values()
        );
        return uniqueBuyers.filter((buyer) => buyer.isDataNdaSigned === true);
    };

    const buyers = useMemo(
        () => getBuyersWithDataNdaSigned(),
        [
            props.currentEngagement?.engagementProcess?.buyers,
            props.availableBuyers,
        ]
    );

    useEffect(() => {
        const initialStates: Record<number, boolean> = {};
        buyers.forEach((buyer) => {
            initialStates[buyer.buyerId] = buyer.isVdrAvailable || false;
        });
        setFeatureStates(initialStates);
    }, [buyers]);

    const tableData = useMemo(() => {
        return buyers.map((buyer) => ({
            ...buyer,
            isVdrAvailable: featureStates[buyer.buyerId] ?? buyer.isVdrAvailable ?? false,
        }));
    }, [buyers, featureStates]);

    const handleSwitchChange = useCallback(async (buyerId: number, checked: boolean) => {
        try {
            dispatch(
                UpdateBuyerIsVdrAvailable({
                    engagementId: props.currentEngagement.id,
                    buyerId: buyerId,
                    isVdrAvailable: checked,
                })
            );

            setFeatureStates((prev) => ({
                ...prev,
                [buyerId]: checked,
            }));
        } catch (error) {
            setFeatureStates((prev) => ({
                ...prev,
                [buyerId]: !checked,
            }));
        }
    }, [dispatch, props.currentEngagement.id]);

    const getColumns = useCallback(() => [
        {
            dataField: 'id',
            text: 'Id',
            hidden: true,
        },
        {
            dataField: 'companyName',
            text: 'Buyer',
            headerClasses: 'buyer-column-header',
        },
        {
            dataField: 'buyerId',
            text: 'Is Available',
            formatter: (cell: number, row: EngagementBuyer & { isVdrAvailable?: boolean }) => (
                <ReactSwitch
                    offColor="#d92550"
                    checked={row.isVdrAvailable ?? false}
                    onChange={(checked) => handleSwitchChange(row.buyerId, checked)}
                    height={22}
                />
            ),
        },
    ], [handleSwitchChange]);

    return (
        <div className="vdr-access">
            <div className="body_inner_content">
                <BootstrapTable
                    bootstrap4
                    keyField="id"
                    data={tableData}
                    columns={getColumns()}
                    striped
                />
            </div>
        </div>
    );
};

export default VDRAccess;
